import { Controller } from 'react-hook-form';
import CalendarIcon from '@untitled-ui/icons-react/build/esm/Calendar';

import { DatePicker, Radio } from 'antd';
import { BaseSelect, StatusTag, UserInitialsCircle } from 'components';
import { DeleteButton } from 'components/Buttons/DeleteButton';
import AddressInput, { Address } from 'components/Inputs/AddressInput';
import FieldError from 'components/Inputs/FieldError';
import { formatMobilePhoneForDisplay, getFullName, highlightText } from 'utils';
import { endOfToday, isAfter, isBefore } from 'date-fns';

interface SelectedIndividualProps {
  user: any;
  searchQuery: string;
  isCompanyPage?: boolean;
  isExistingVisible?: boolean;
  areOptionsVisible?: boolean;
  handleDisplayInputs?: (event: any) => void;
  isStatusVisible?: boolean;
  isSelectVisible?: boolean;
  index?: number;
  selectOptions?: any[];
  onSelect?: (value: string) => void;
  handleDisplayAddresModal?: (event: any) => void;
  isCheckingEmail?: boolean;
  isSelected?: boolean;
  onSelectIndividual?: () => void;
  hasDateOfBirth?: boolean;
  control?: any;
  handleAddress?: (value: Address) => void;
  isAssociated?: boolean;
  handleDob?: (value: any) => void;
}

export const SelectedIndividual = ({
  user,
  searchQuery,
  isExistingVisible,
  areOptionsVisible,
  handleDisplayInputs,
  isStatusVisible = true,
  isSelectVisible = false,
  selectOptions = [],
  onSelect,
  isCheckingEmail,
  isSelected = true,
  onSelectIndividual,
  handleDisplayAddresModal,
  hasDateOfBirth = false,
  control,
  isCompanyPage = false,
  index,
  handleAddress,
  handleDob,
  isAssociated = false,
}: SelectedIndividualProps) => {
  const hiddenStyle = !areOptionsVisible && !isCheckingEmail ? !isExistingVisible : false;
  const displayStyle = `${!areOptionsVisible ? 'rounded-md bg-gray-100 py-2.5 px-2' : ''} ${isCompanyPage ? 'bg-white text-gray-900' : ' text-gray-500'}`;

  const {
    firstName,
    lastName,
    mobilePhone,
    email,
    status,
    address,
    dateOfBirth,
    roleName,
    ownershipPercentage,
    beneficiallyHeld,
  } = user ?? {};

  const hasAddress = address !== undefined && address !== null && address?.trim() !== '';
  const hasDob =
    dateOfBirth !== undefined &&
    dateOfBirth !== null &&
    dateOfBirth !== '' &&
    dateOfBirth !== '0001-01-01';

  return (
    <div className="flex flex-col w-full">
      <div
        className={`flex justify-between items-center w-full ${displayStyle}`}
        hidden={hiddenStyle}
      >
        <div
          className={`flex ${isCompanyPage ? 'w-full' : 'gap-2'} ${onSelectIndividual ? 'cursor-pointer' : ''}`}
          onClick={onSelectIndividual}
        >
          {!isCompanyPage ? (
            isCheckingEmail ? (
              <Radio type="radio" checked={isSelected} onChange={() => onSelectIndividual?.()} />
            ) : (
              <UserInitialsCircle data={user} />
            )
          ) : null}

          <div className={`${isCompanyPage ? 'w-[30%] truncate' : 'flex flex-col'}`}>
            <span className="font-semibold text-sm">
              {highlightText(getFullName(firstName, lastName), searchQuery)}
            </span>
            <div className="font-normal text-xs overflow-hidden text-ellipsis whitespace-nowrap">
              {!isCompanyPage
                ? [email, formatMobilePhoneForDisplay(mobilePhone)].filter((s) => s).join(' - ')
                : `${email}`}
            </div>
            {!isCompanyPage && address && <div className="font-normal text-xs">{address}</div>}
          </div>

          {isCompanyPage && (
            <>
              <div className="w-[30%] font-semibold text-xs inline-block break-words">
                {address ? address : '-'}
              </div>
              <div className="w-[20%] font-semibold text-xs inline-block break-words">
                {roleName ? roleName : '-'}
              </div>
              <div className="w-[20%] font-semibold text-xs inline-block break-words">
                {beneficiallyHeld ? 'Yes' : 'No'}
              </div>
              <div className="w-[20%] font-semibold text-xs inline-block break-words">
                {ownershipPercentage ? `${ownershipPercentage}%` : '-'}
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2 justify-top">
          {isSelectVisible && (
            <BaseSelect
              options={selectOptions}
              className="min-w-40"
              placeholder="Select relationship"
              onSelect={onSelect}
            />
          )}

          {isStatusVisible && <StatusTag status={status ?? ''} />}
          {!isCompanyPage && (
            <DeleteButton
              onClick={handleDisplayInputs}
              areOptionsVisible={areOptionsVisible || isCheckingEmail}
            />
          )}
        </div>
      </div>
      {!(areOptionsVisible || isCheckingEmail) && (!hasAddress || !hasDob) && (
        <div className={`flex gap-2 ${displayStyle} ${isCompanyPage ? 'border-b' : 'border-t'}`}>
          {!hasAddress && (
            <div className="font-semibold text-xs text-gray-900 gap-2">
              <span className="mb-2 block">Address *</span>
              <AddressInput
                name={
                  isCompanyPage
                    ? `address-${index}`
                    : isAssociated
                      ? 'associatedAddressInput'
                      : 'addressInput'
                }
                placeholder="Enter address..."
                className={`w-[400px] h-10`}
                onChange={handleAddress}
                isSelectedIndividual={true}
              />
            </div>
          )}
          {!hasDob && (
            <div className="font-semibold text-xs text-gray-900 gap-2">
              <span className="mb-2 block">Date of birth *</span>
              <Controller
                name={
                  isCompanyPage
                    ? `dateOfBirth-${index}`
                    : isAssociated
                      ? 'associatedDateOfBirth'
                      : 'dateOfBirth'
                }
                rules={{ required: 'Date of birth is required' }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <DatePicker
                      placeholder="Select date of birth..."
                      onChange={(date) => {
                        field.onChange(date);
                        handleDob?.(date);
                      }}
                      value={field.value || null}
                      format="DD/MM/YYYY"
                      suffixIcon={
                        <div className="border-l border-gray-300 h-10 flex items-center mt-0.5">
                          <CalendarIcon className="text-gray-700 h-5 w-5 ml-2.5" />
                        </div>
                      }
                      className={`h-10 w-[240px] ${error ? 'border-red-500' : ''}`}
                      disabledDate={(current: any) => {
                        const minDate = new Date(1900, 0, 1);
                        return (
                          current && (isAfter(current.toDate(), endOfToday()) || isBefore(current.toDate(), minDate))
                        );
                      }}
                    />
                    <FieldError message={error?.message} />
                  </div>
                )}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
